// Utility function for exporting data to CSV
export const exportToCSV = (data, filename, headers) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  // Create CSV content
  const csvContent = [
    headers.map(h => h.label).join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header.key] || '';
        // Handle date formatting
        if (header.type === 'date' && value) {
          return `"${new Date(value).toLocaleDateString()}"`;
        }
        // Handle numeric formatting
        if (header.type === 'numeric' && value !== '') {
          return parseFloat(value).toFixed(3);
        }
        // Handle string values with quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    )
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.href) {
    URL.revokeObjectURL(link.href);
  }
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Common header definitions for different report types
export const reportHeaders = {
  rawMaterialStock: [
    { key: 'stock_code', label: 'Stock Code' },
    { key: 'stock_name', label: 'Stock Name' },
    { key: 'qty', label: 'Qty', type: 'numeric' },
    { key: 'weight', label: 'Weight', type: 'numeric' },
    { key: 'pure_weight', label: 'Pure Weight', type: 'numeric' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'created_at', label: 'Created Date', type: 'date' }
  ],
  
  rawGoldIssued: [
    { key: 'batch_number', label: 'Batch Number' },
    { key: 'product', label: 'Product' },
    { key: 'hm_purity', label: 'HM Purity' },
    { key: 'qty', label: 'Qty', type: 'numeric' },
    { key: 'weight', label: 'Weight', type: 'numeric' },
    { key: 'pure_weight', label: 'Pure Weight', type: 'numeric' },
    { key: 'department_name', label: 'Department' },
    { key: 'worker_name', label: 'Worker' },
    { key: 'issue_date', label: 'Issue Date', type: 'date' }
  ],
  
  stoneIssued: [
    { key: 'stone_code', label: 'Stone Code' },
    { key: 'stone_name', label: 'Stone Name' },
    { key: 'qty', label: 'Qty', type: 'numeric' },
    { key: 'weight_gms', label: 'Weight (Gms)', type: 'numeric' },
    { key: 'weight_cts', label: 'Weight (Cts)', type: 'numeric' },
    { key: 'department_name', label: 'Department' },
    { key: 'worker_name', label: 'Worker' },
    { key: 'issue_date', label: 'Issue Date', type: 'date' }
  ],
  
  goldStockAdded: [
    { key: 'created_at', label: 'Date & Time', type: 'date' },
    { key: 'action_type', label: 'Action Type' },
    { key: 'stock_code', label: 'Stock Code' },
    { key: 'stock_name', label: 'Stock Name' },
    { key: 'stock_type', label: 'Stock Type' },
    { key: 'purity', label: 'Purity' },
    { key: 'color', label: 'Color' },
    { key: 'stone_type', label: 'Stone Type' },
    { key: 'qty', label: 'Qty', type: 'numeric' },
    { key: 'weight', label: 'Weight', type: 'numeric' },
    { key: 'pure_weight', label: 'Pure Weight', type: 'numeric' },
    { key: 'weight_cts', label: 'Weight CTS', type: 'numeric' },
    { key: 'size', label: 'Size' },
    { key: 'ref_no', label: 'Reference' },
    { key: 'remarks', label: 'Remarks' },
    { key: 'created_by', label: 'Created By' },
    { key: 'batch_number', label: 'Batch Number' },
    { key: 'transaction_reference', label: 'Transaction Ref' },
    { key: 'previous_qty', label: 'Previous Qty', type: 'numeric' },
    { key: 'previous_weight', label: 'Previous Weight', type: 'numeric' },
    { key: 'new_qty', label: 'New Qty', type: 'numeric' },
    { key: 'new_weight', label: 'New Weight', type: 'numeric' }
  ],
  
  stoneStockAdded: [
    { key: 'created_at', label: 'Date & Time', type: 'date' },
    { key: 'action_type', label: 'Action Type' },
    { key: 'stock_code', label: 'Stock Code' },
    { key: 'stock_name', label: 'Stock Name' },
    { key: 'stock_type', label: 'Stock Type' },
    { key: 'karatage', label: 'Karatage' },
    { key: 'color', label: 'Color' },
    { key: 'stone_type', label: 'Stone Type' },
    { key: 'qty', label: 'Qty', type: 'numeric' },
    { key: 'weight_gms', label: 'Weight Gms', type: 'numeric' },
    { key: 'pure_weight', label: 'Pure Weight', type: 'numeric' },
    { key: 'weight_cts', label: 'Weight CTS', type: 'numeric' },
    { key: 'size', label: 'Size' },
    { key: 'ref_no', label: 'Reference' },
    { key: 'remarks', label: 'Remarks' },
    { key: 'created_by', label: 'Created By' },
    { key: 'batch_number', label: 'Batch Number' },
    { key: 'transaction_reference', label: 'Transaction Ref' },
    { key: 'previous_qty', label: 'Previous Qty', type: 'numeric' },
    { key: 'previous_weight', label: 'Previous Weight', type: 'numeric' },
    { key: 'new_qty', label: 'New Qty', type: 'numeric' },
    { key: 'new_weight', label: 'New Weight', type: 'numeric' }
  ],
  
  departmentWiseProduction: [
    { key: 'batch_number', label: 'Batch Number' },
    { key: 'product', label: 'Product' },
    { key: 'hm_purity', label: 'HM Purity' },
    { key: 'qty', label: 'Qty', type: 'numeric' },
    { key: 'net_weight', label: 'Net Weight', type: 'numeric' },
    { key: 'gross_weight', label: 'Gross Weight', type: 'numeric' },
    { key: 'net_weight_purity', label: 'Pure Weight', type: 'numeric' },
    { key: 'bom_dia_qty', label: 'BOM Dia Qty', type: 'numeric' },
    { key: 'bom_clr_qty', label: 'BOM Clr Qty', type: 'numeric' },
    { key: 'issue_tot_qty', label: 'Issue Tot Qty', type: 'numeric' },
    { key: 'stone_weight', label: 'Stn Wt/Gms', type: 'numeric' },
    { key: 'stone_weight_cts', label: 'Stn Wt/Cts', type: 'numeric' },
    { key: 'department_name', label: 'Department Name' },
    { key: 'worker_name', label: 'Worker Name' },
    { key: 'received_weight', label: 'Received Weight', type: 'numeric' },
    { key: 'issue_weight', label: 'Issue Weight', type: 'numeric' },
    { key: 'bip_net_weight', label: 'Net Weight', type: 'numeric' },
    { key: 'bip_pure_weight', label: 'Pure Weight', type: 'numeric' },
    { key: 'removal_weight', label: 'Removal Weight', type: 'numeric' },
    { key: 'bip_stone_weight', label: 'Stone Weight', type: 'numeric' },
    { key: 'stone_qty', label: 'Stone Qty', type: 'numeric' },
    { key: 'dia_qty', label: 'Dia Qty', type: 'numeric' },
    { key: 'dia_broken', label: 'Dia Broken', type: 'numeric' },
    { key: 'color_stone_qty', label: 'Color Stone Qty', type: 'numeric' },
    { key: 'color_stone_broken', label: 'Color Stone Broken', type: 'numeric' },
    { key: 'nick_plate_qty', label: 'Nick Plate Qty', type: 'numeric' },
    { key: 'nick_plate_broken', label: 'Nick Plate Broken', type: 'numeric' },
    { key: 'pearl_weight', label: 'Pearl Weight', type: 'numeric' },
    { key: 'pearl_qty', label: 'Pearl Qty', type: 'numeric' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'jo_number', label: 'JO No' },
    { key: 'jo_date', label: 'JO Date', type: 'date' },
    { key: 'production_route', label: 'Prod Route' },
    { key: 'production_date', label: 'Production Date', type: 'date' },
    { key: 'completion_date', label: 'Completion Date', type: 'date' },
    { key: 'production_status', label: 'Production Status' },
    { key: 'production_remarks', label: 'Production Remarks' },
    { key: 'created_by', label: 'Created By' },
    { key: 'created_at', label: 'Created At' }
  ],
  
  departmentWisePending: [
    { key: 'status', label: 'Status' },
    { key: 'batch_number', label: 'Batch Number' },
    { key: 'product', label: 'Product' },
    { key: 'hm_purity', label: 'HM Purity' },
    { key: 'qty', label: 'Qty', type: 'numeric' },
    { key: 'net_weight', label: 'Net Weight', type: 'numeric' },
    { key: 'gross_weight', label: 'Gross Weight', type: 'numeric' },
    { key: 'net_weight_purity', label: 'Pure Weight', type: 'numeric' },
    { key: 'department_name', label: 'Department' },
    { key: 'worker_name', label: 'Worker' },
    { key: 'jo_date', label: 'JO Date', type: 'date' }
  ],
  
  rawStockHistory: [
    { key: 'created_at', label: 'Date & Time', type: 'date' },
    { key: 'action_type', label: 'Action' },
    { key: 'stock_code', label: 'Stock Code' },
    { key: 'stock_name', label: 'Stock Name' },
    { key: 'stock_type_name', label: 'Stock Type' },
    { key: 'qty', label: 'Quantity', type: 'numeric' },
    { key: 'weight', label: 'Weight', type: 'numeric' },
    { key: 'stone_type_name', label: 'Stone Type' },
    { key: 'karatage_name', label: 'Karatage' },
    { key: 'color_name', label: 'Color' },
    { key: 'size', label: 'Size' },
    { key: 'reference_number', label: 'Reference' },
    { key: 'remarks', label: 'Remarks' },
    { key: 'created_by', label: 'Created By' },
    { key: 'batch_number', label: 'Batch Number' },
    { key: 'transaction_reference', label: 'Transaction Ref' },
    { key: 'previous_qty', label: 'Previous Qty', type: 'numeric' },
    { key: 'previous_weight', label: 'Previous Weight', type: 'numeric' },
    { key: 'new_qty', label: 'New Qty', type: 'numeric' },
    { key: 'new_weight', label: 'New Weight', type: 'numeric' }
  ]
}; 